import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    barChartOutline,
    americanFootballOutline,
    refreshOutline,
    shieldCheckmarkOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionGame,
    GameDetail,
    TeamDetail,
    getGameDetail,
    getTeamDetail
} from "../services/mexfbaApi";
import * as Components from "./StatsFunction";
import "./TeamStatsTeam.css";

interface RouteParams {
    id: string;
}

interface TeamStats {
    id: string;
    rush: number;
    pass: number;
    tdR: number;
    tdP: number;
    rushD: number;
    passD: number;
    tdRD: number;
    tdPD: number;
    fr: number;
    int: number;
    attXp: number;
    xp: number;
    attFg: number;
    fg: number;
    ydsKR: number;
    ydsPR: number;
    tdKR: number;
    tdPR: number;
    kb: number;
    sack: number;
    conv: number;
    attR: number;
    attP: number;
    comp: number;
    penOff: number;
    penDef: number;
    ydPenOff: number;
    ydPenDef: number;
}

interface StatisticsByVenue {
    home: TeamStats;
    away: TeamStats;
    gamesAtHome: number;
    gamesAway: number;
}

interface StatRow {
    label: string;
    home: number | string;
    away: number | string;
}

function getGames(detail: TeamDetail): CompetitionGame[] {
    if (!detail.Partidos) {
        return [];
    }

    const games = Array.isArray(detail.Partidos)
        ? detail.Partidos
        : Object.values(detail.Partidos);

    const competitionId = detail.Info.comp_id;

    return games.filter(function (game) {
        return (
            !competitionId ||
            !game.id_comp ||
            game.id_comp === competitionId
        );
    });
}

function getPlays(gameDetail: GameDetail): any[] {
    if (!gameDetail.Jugadas) {
        return [];
    }

    return Array.isArray(gameDetail.Jugadas)
        ? gameDetail.Jugadas
        : Object.values(gameDetail.Jugadas);
}
function hasNumericScore(value?: string | number): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}

function isFinishedGame(game: CompetitionGame): boolean {
    return (
        hasNumericScore(game.resLocal) &&
        hasNumericScore(game.resVisita)
    );
}

function createStats(teamId: string): TeamStats {
    const stats: TeamStats[] = [];

    Components.setStatsTeamAlt(teamId, stats);

    return stats[0];
}

function divide(numerator: number, denominator: number): string {
    if (!denominator) {
        return "0.0";
    }

    return (numerator / denominator).toFixed(1);
}

function renderValue(value: number | string): string {
    return typeof value === "number" ? String(value) : value;
}

const TeamStatsTeam: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [teamDetail, setTeamDetail] =
        useState<TeamDetail | null>(null);
    const [statistics, setStatistics] =
        useState<StatisticsByVenue | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadStatistics = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const detail = await getTeamDetail(id, forceRefresh);
                const games = getGames(detail).filter(isFinishedGame);


                const gameDetails = await Promise.all(
                    games.map(function (game) {
                        return getGameDetail(game.id, forceRefresh);
                    })
                );

                const home = createStats(id);
                const away = createStats(id);

                let gamesAtHome = 0;
                let gamesAway = 0;

                for (const gameDetail of gameDetails) {
                    const gameData = gameDetail.Data as {
                        id_local?: string;
                    };

                    const isHome = gameData?.id_local === id;
                    const currentStats = isHome ? home : away;

                    if (isHome) {
                        gamesAtHome++;
                    } else {
                        gamesAway++;
                    }

                    for (const play of getPlays(gameDetail)) {
                        if (play.ofensiva === id) {
                            Components.getOfence(play, [currentStats]);
                            Components.getPenalty(play, [currentStats]);
                            Components.getSpecialK(play, [currentStats]);
                        } else {
                            Components.getDefencee(
                                play,
                                [currentStats],
                                id
                            );
                            Components.getSpecialR(
                                play,
                                [currentStats],
                                id
                            );
                        }
                    }
                }

                setTeamDetail(detail);
                setStatistics({
                    home,
                    away,
                    gamesAtHome,
                    gamesAway
                });
            } catch (requestError) {
                console.error(
                    "No fue posible cargar las estadísticas del equipo:",
                    requestError
                );

                setError(
                    "No fue posible calcular las estadísticas del equipo."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadStatistics();
        },
        [loadStatistics]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadStatistics(true);
        event.detail.complete();
    }

    const offenseRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Yardas totales",
                    home: home.pass + home.rush,
                    away: away.pass + away.rush
                },
                {
                    label: "Jugadas",
                    home: home.attP + home.attR,
                    away: away.attP + away.attR
                },
                {
                    label: "Yardas por jugada",
                    home: divide(
                        home.pass + home.rush,
                        home.attP + home.attR
                    ),
                    away: divide(
                        away.pass + away.rush,
                        away.attP + away.attR
                    )
                },
                {
                    label: "Touchdowns",
                    home: home.tdP + home.tdR,
                    away: away.tdP + away.tdR
                },
                {
                    label: "Conversiones de 2",
                    home: home.conv,
                    away: away.conv
                }
            ];
        },
        [statistics]
    );

    const passingRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Completos / intentos",
                    home: `${home.comp}/${home.attP}`,
                    away: `${away.comp}/${away.attP}`
                },
                {
                    label: "Yardas por pase",
                    home: home.pass,
                    away: away.pass
                },
                {
                    label: "Yardas por completo",
                    home: divide(home.pass, home.comp),
                    away: divide(away.pass, away.comp)
                },
                {
                    label: "TD por pase",
                    home: home.tdP,
                    away: away.tdP
                }
            ];
        },
        [statistics]
    );

    const rushingRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Acarreos",
                    home: home.attR,
                    away: away.attR
                },
                {
                    label: "Yardas por carrera",
                    home: home.rush,
                    away: away.rush
                },
                {
                    label: "Yardas por acarreo",
                    home: divide(home.rush, home.attR),
                    away: divide(away.rush, away.attR)
                },
                {
                    label: "TD por carrera",
                    home: home.tdR,
                    away: away.tdR
                }
            ];
        },
        [statistics]
    );

    const defenseRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Intercepciones",
                    home: home.int,
                    away: away.int
                },
                {
                    label: "Fumbles recuperados",
                    home: home.fr,
                    away: away.fr
                },
                {
                    label: "Capturas",
                    home: home.sack,
                    away: away.sack
                },
                {
                    label: "Yardas permitidas",
                    home: home.rushD + home.passD,
                    away: away.rushD + away.passD
                },
                {
                    label: "TD permitidos",
                    home: home.tdRD + home.tdPD,
                    away: away.tdRD + away.tdPD
                }
            ];
        },
        [statistics]
    );

    const specialTeamsRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Puntos extra",
                    home: `${home.xp}/${home.attXp}`,
                    away: `${away.xp}/${away.attXp}`
                },
                {
                    label: "Goles de campo",
                    home: `${home.fg}/${home.attFg}`,
                    away: `${away.fg}/${away.attFg}`
                },
                {
                    label: "Yardas en retornos",
                    home: home.ydsKR + home.ydsPR,
                    away: away.ydsKR + away.ydsPR
                },
                {
                    label: "TD en retornos",
                    home: home.tdKR + home.tdPR,
                    away: away.tdKR + away.tdPR
                },
                {
                    label: "Patadas bloqueadas",
                    home: home.kb,
                    away: away.kb
                }
            ];
        },
        [statistics]
    );

    const penaltyRows = useMemo<StatRow[]>(
        function () {
            if (!statistics) {
                return [];
            }

            const { home, away } = statistics;

            return [
                {
                    label: "Castigos",
                    home: home.penOff + home.penDef,
                    away: away.penOff + away.penDef
                },
                {
                    label: "Yardas de castigo",
                    home: home.ydPenOff + home.ydPenDef,
                    away: away.ydPenOff + away.ydPenDef
                }
            ];
        },
        [statistics]
    );

    function renderStatSection(
        title: string,
        subtitle: string,
        rows: StatRow[]
    ) {
        return (
            <section className="team-team-stats-card">
                <header>
                    <div>
                        <span>{subtitle}</span>
                        <h2>{title}</h2>
                    </div>

                    <IonIcon icon={barChartOutline} />
                </header>

                <div className="team-team-stats-table">
                    <div className="team-team-stats-table__head">
                        <strong>LOCAL</strong>
                        <span>ESTADÍSTICA</span>
                        <strong>VISITA</strong>
                    </div>

                    {rows.map(function (row) {
                        return (
                            <div
                                className="team-team-stats-row"
                                key={row.label}
                            >
                                <strong>{renderValue(row.home)}</strong>
                                <span>{row.label}</span>
                                <strong>{renderValue(row.away)}</strong>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-team-stats-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !teamDetail || !statistics) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Estadísticas</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-team-stats-content">
                    <div className="team-team-stats-empty">
                        <IonIcon icon={americanFootballOutline} />
                        <strong>No fue posible cargar las estadísticas</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = teamDetail.Info;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="team-team-stats-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al equipo"
                            onClick={function () {
                                history.push(`/team/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Estadísticas</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadStatistics(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="team-team-stats-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Calculando estadísticas..."
                    />
                </IonRefresher>

                <section
                    className="team-team-stats-hero"
                    style={
                        info.color
                            ? {
                                  background: `linear-gradient(135deg, ${info.color}, #173e70)`
                              }
                            : undefined
                    }
                >
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="team-team-stats-hero__placeholder">
                            {info.nombre?.charAt(0) || "E"}
                        </div>
                    )}

                    <div>
                        <span>ESTADÍSTICAS DEL EQUIPO</span>
                        <h1>{info.nombre}</h1>
                        <p>{info.comp_nombre || "Temporada actual"}</p>
                    </div>
                </section>

                <section className="team-team-stats-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/team/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/table/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeam/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton>Equipo</IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/rosterTeam/${id}`);
                        }}
                    >
                        Plantel
                    </IonButton>
                </section>

                <section className="team-team-stats-summary">
                    <IonIcon icon={shieldCheckmarkOutline} />

                    <div>
                        <span>COMPARATIVA POR SEDE</span>
                        <strong>
                            {statistics.gamesAtHome} local ·{" "}
                            {statistics.gamesAway} visita
                        </strong>
                    </div>
                </section>

                <main className="team-team-stats-sections">
                    {renderStatSection(
                        "Ofensiva",
                        "PRODUCCIÓN TOTAL",
                        offenseRows
                    )}

                    {renderStatSection(
                        "Pase",
                        "ATAQUE AÉREO",
                        passingRows
                    )}

                    {renderStatSection(
                        "Carrera",
                        "ATAQUE TERRESTRE",
                        rushingRows
                    )}

                    {renderStatSection(
                        "Defensiva",
                        "RECUPERACIONES Y PRESIÓN",
                        defenseRows
                    )}

                    {renderStatSection(
                        "Equipos especiales",
                        "PATADAS Y RETORNOS",
                        specialTeamsRows
                    )}

                    {renderStatSection(
                        "Castigos",
                        "DISCIPLINA",
                        penaltyRows
                    )}
                </main>
            </IonContent>
        </IonPage>
    );
};

export default TeamStatsTeam;
